import { forwardRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { IconXmark } from "@/components/icons/Icons";
import { haptic } from "@/lib/haptics";

export const GlassPanel = forwardRef(function GlassPanel({ className, as: As = "div", children, ...rest }, ref) {
  return (
    <As ref={ref} className={cn("pk-glass rounded-[22px]", className)} {...rest}>
      {children}
    </As>
  );
});

const VARIANTS = {
  primary: "bg-white text-black hover:bg-white/90 shadow-[0_10px_30px_rgba(0,0,0,.45)]",
  glass: "pk-glass text-white hover:bg-white/[.10]",
  ghost: "text-white/80 hover:text-white hover:bg-white/[.07]",
  danger: "bg-[#FF453A]/15 text-[#FF6961] border border-[#FF453A]/25 hover:bg-[#FF453A]/25",
};
const SIZES = {
  sm: "h-9 px-3.5 text-[13px] gap-1.5 rounded-[12px]",
  md: "h-11 px-5 text-[15px] gap-2 rounded-[14px]",
  lg: "h-12 px-6 text-[16px] gap-2.5 rounded-[16px]",
  icon: "h-11 w-11 rounded-full justify-center",
  iconSm: "h-9 w-9 rounded-full justify-center",
};

export const GlassButton = forwardRef(function GlassButton({ variant = "glass", size = "md", className, children, disabled, onClick, ...rest }, ref) {
  const reduce = useReducedMotion();
  return (
    <motion.button
      ref={ref}
      type="button"
      whileHover={disabled || reduce ? undefined : { scale: 1.01 }}
      whileTap={disabled || reduce ? undefined : { scale: 0.97 }}
      transition={{ type: "spring", stiffness: 520, damping: 34 }}
      disabled={disabled}
      onClick={(e) => {
        haptic();
        onClick?.(e);
      }}
      className={cn(
        "inline-flex select-none items-center justify-center font-semibold tracking-[-0.01em] outline-none transition-[background-color,color,opacity,box-shadow] duration-200 disabled:pointer-events-none disabled:opacity-45",
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...rest}
    >
      {children}
    </motion.button>
  );
});

export function GlassModal({ open, onClose, children, className, label = "Oyna", testId = "glass-modal" }) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] grid place-items-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} role="dialog" aria-modal="true" aria-label={label} data-testid={testId}>
          <button aria-label="Yopish" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={`${testId}-backdrop`} />
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, filter: "blur(8px)" }}
            transition={{ duration: 0.32, ease: [0.2, 0.9, 0.2, 1] }}
            className={cn("pk-glass-strong relative w-full max-w-3xl overflow-hidden rounded-[26px]", className)}
          >
            <button onClick={onClose} aria-label="Yopish" data-testid={`${testId}-close`} className="pk-glass absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full text-white/90 hover:text-white">
              <IconXmark size={18} />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

/** Segmented control (replaces native selects). */
export function Segmented({ options, value, onChange, className, testId = "segmented", size = "md" }) {
  return (
    <div role="radiogroup" className={cn("pk-glass inline-flex rounded-full p-1", className)} data-testid={testId}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={active}
            onClick={() => {
              haptic(6);
              onChange(o.value);
            }}
            data-testid={`${testId}-${o.value}`}
            className={cn(
              "relative rounded-full font-semibold transition-colors duration-200",
              size === "sm" ? "h-8 px-3 text-[12.5px]" : "h-9 px-4 text-[13.5px]",
              active ? "text-black" : "text-white/70 hover:text-white"
            )}
          >
            {active && <motion.span layoutId={`${testId}-pill`} className="absolute inset-0 rounded-full bg-white" transition={{ type: "spring", stiffness: 500, damping: 38 }} />}
            <span className="relative">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Chip({ active, children, className, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-4 text-[13.5px] font-semibold transition-[background-color,color,border-color] duration-200 active:scale-[.97]",
        active ? "border-white bg-white text-black" : "border-white/10 bg-white/[.05] text-white/75 hover:bg-white/[.09] hover:text-white",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
